/** Floating status for the background validation job; shows the console's current state on every page. */
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Upload,
  X,
  Radar,
} from "lucide-react";
import { useProcessingJobStore } from "../../stores/processingJobStore";
import { PIPELINE_STAGES } from "../pages/ContentRegistry/processingPipeline";
import {
  JOB_PHASE,
  IN_FLIGHT_PHASES,
  type JobPhase,
} from "../pages/ContentRegistry/constants";
import { useComplianceAssistantStore } from "./ComplianceAssistant/store";

interface BubbleMeta {
  icon: typeof Loader2;
  spin: boolean;
  label: string;
  cls: string;
  ring: string;
}

const INDIGO_META = {
  cls: "text-indigo-600 dark:text-indigo-400",
  ring: "ring-indigo-200/70 dark:ring-indigo-500/25",
} as const;

const BUBBLE_META: Record<JobPhase, BubbleMeta> = {
  [JOB_PHASE.RUNNING]: {
    icon: Loader2,
    spin: true,
    label: "Validating document",
    ...INDIGO_META,
  },
  [JOB_PHASE.UPLOADING]: {
    icon: Upload,
    spin: false,
    label: "Uploading",
    ...INDIGO_META,
  },
  [JOB_PHASE.VALIDATING_LAYERS]: {
    icon: Loader2,
    spin: true,
    label: "Validating layers",
    ...INDIGO_META,
  },
  [JOB_PHASE.COMPLETE]: {
    icon: CheckCircle2,
    spin: false,
    label: "Validation complete",
    cls: "text-emerald-600 dark:text-emerald-400",
    ring: "ring-emerald-200/70 dark:ring-emerald-500/25",
  },
  [JOB_PHASE.ERROR]: {
    icon: AlertTriangle,
    spin: false,
    label: "Validation failed",
    cls: "text-red-600 dark:text-red-400",
    ring: "ring-red-200/70 dark:ring-red-500/25",
  },
};

export function ProcessingStatusBubble() {
  const job = useProcessingJobStore((s) => s.job);
  const minimized = useProcessingJobStore((s) => s.minimized);
  const restore = useProcessingJobStore((s) => s.restore);
  const cancel = useProcessingJobStore((s) => s.cancel);
  const dismiss = useProcessingJobStore((s) => s.dismiss);
  const assistantOpen = useComplianceAssistantStore((s) => s.open);

  if (!job || !minimized) return null;

  const meta = BUBBLE_META[job.phase];
  const Icon = meta.icon;
  const busy = IN_FLIGHT_PHASES.includes(job.phase);
  const stageLabel =
    job.phase === JOB_PHASE.RUNNING
      ? PIPELINE_STAGES[Math.min(job.stage, PIPELINE_STAGES.length - 1)]?.label
      : null;

  return (
    <div
      className={`fixed bottom-5 z-[61] max-w-[calc(100vw-2rem)] ${
        assistantOpen ? "left-4 sm:left-6" : "right-4 sm:right-5"
      }`}
    >
      <div
        className={`flex items-center gap-3 rounded-xl bg-card px-3 py-2.5 shadow-lg ring-1 ${meta.ring}`}
      >
        <button
          type="button"
          onClick={restore}
          title="Open the processing console"
          className="flex min-w-0 items-center gap-3 text-left"
        >
          <span
            className={`relative grid size-9 flex-none place-items-center rounded-lg bg-[#f8fafc] dark:bg-white/[0.04] ${meta.cls}`}
          >
            <Icon className={`size-4 ${meta.spin ? "animate-spin" : ""}`} />
            {busy && (
              <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-indigo-500 ring-2 ring-card" />
            )}
          </span>
          <span className="min-w-0">
            <span className={`block text-xs font-bold ${meta.cls}`}>
              {meta.label}
            </span>
            <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Radar className="size-3 flex-none" />
              <span className="truncate max-w-[190px]">
                {job.documentTitle || job.fileName || "Document"}
              </span>
            </span>
            {stageLabel && (
              <span className="mt-0.5 block truncate max-w-[220px] text-[11px] text-muted-foreground">
                {stageLabel}
              </span>
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={() => (busy ? cancel() : dismiss())}
          title={busy ? "Cancel" : "Dismiss"}
          className="flex-none rounded-md p-1 text-muted-foreground transition-colors hover:bg-[#f1f5f9] hover:text-foreground dark:hover:bg-white/[0.06]"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
